import type { PHC_ENV } from "@phc/common"
import {
  getAssetServiceDomain,
  getCapDomain,
  getAiServiceDomain,
  getThreatAnalyticsDomain,
} from "@phc/common"

// ── Route categories ─────────────────────────────────────────────
// Every route must belong to exactly one category.

/** Routes that simply redirect to another route. */
const REDIRECT_ROUTES = {
  BASE: "/",
  HOME: "/home",
} as const

/** Routes accessible without authentication. */
const UNPROTECTED_ROUTE_CONFIG = {
  SIGN_IN: "/sign-in",
  /** Route for invite validation */
  LOGIN: "/login",
  ERROR: "/error",
  PRIVACY_POLICY: "/privacy-policy",
  TERMS_AND_CONDITIONS: "/terms-and-conditions",
  NOT_FOUND: "/not-found",
} as const

/** Authenticated routes that are always available. */
const CORE_ROUTES = {
  PULSE: "/pulse",
  BIOTHREAT_SIGNALS: "/biothreat-signals",
  BIOTHREAT_SIGNAL_DETAIL: "/biothreat-signals/:source/:signalId",
  WASTEWATER_OAHU: "/wastewater/oahu",
  PREFERENCES: "/preferences",
  DOCUMENTATION: "/documentation",
  SOURCES: "/sources",
  FAQ: "/faq",
  FAQ_POST: "/faq/:slug",
} as const

/** Routes only accessible when the `allowNonBetaRoutes` flag is enabled. */
const FULL_ACCESS_ROUTE_CONFIG = {
  GUIDANCE: "/guidance",
  GUIDANCE_POST: "/guidance/:slug",
  ANALYSES: "/analyses",
  ANALYSIS_POST: "/analyses/:slug",
  CRITICAL_THREATS: "/critical-threats",
  CRITICAL_THREAT: "/critical-threat/:slug",
  BIO_MESH: "/bio-mesh",
  COP: "/cop",
  ASSETS: "/assets",
  ASSET: "/assets/:asset_id",
  LOCATION_DETAILS: "/location-details",
  ORGANIZATIONS: "/organizations",
  WINGMAN: "/wingman",
  WINGMAN_CHAT: "/wingman/:chatId?",
} as const

// ── Composed exports ─────────────────────────────────────────────

export const ROUTES = {
  ...REDIRECT_ROUTES,
  ...UNPROTECTED_ROUTE_CONFIG,
  ...CORE_ROUTES,
  ...FULL_ACCESS_ROUTE_CONFIG,
} as const

export const UNPROTECTED_ROUTES: string[] = Object.values(
  UNPROTECTED_ROUTE_CONFIG
)

export const FULL_ACCESS_ROUTES: string[] = Object.values(
  FULL_ACCESS_ROUTE_CONFIG
) as string[]

export const FAQ_POST_SLUGS = {
  PHC_NAVIGATOR: "phc-navigator",
  SOURCE_CONFIDENCE: "source-confidence-a-credibility-assessment",
  WASTEWATER_METHODLOGY: "ww-methodology",
} as const

export const BETA_ROUTES: string[] = [
  ROUTES.WASTEWATER_OAHU,
  ROUTES.BIOTHREAT_SIGNALS,
  ROUTES.BIOTHREAT_SIGNAL_DETAIL,
  ROUTES.WINGMAN,
  ROUTES.WINGMAN_CHAT,
] satisfies (typeof ROUTES)[keyof typeof ROUTES][] as string[]
export const stringifySearchParams = (params: object) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (!value) return acc
    const encodedKey = encodeURIComponent(key)
    const encodedValue = encodeURIComponent(String(value))
    return `${acc}${acc ? "&" : "?"}${encodedKey}=${encodedValue}`
  }, "")
}

export const ENVIRONMENTS: Record<PHC_ENV, string> = {
  prd: "production_old",
  dev: "development",
  prod: "production",
  local: "local",
}

export const PREFERENCES_SERVICE_API = getAssetServiceDomain()
export const CAP_URL = import.meta.env.VITE_CAP_URL_DEV ?? getCapDomain()
export const MAP_ASSETS_URL = `${CAP_URL}/map`
export const AI_SERVICE_URL =
  import.meta.env.VITE_AI_SERVICE_URL ?? getAiServiceDomain()
export const THREAT_ANALYTICS_URL = getThreatAnalyticsDomain()

export const claims = {
  roles: "https://thepublichealthco.com/v1/roles",
  user: "https://thepublichealthco.com/v1/user",
  org: "https://thepublichealthco.com/v1/organization",
} as const

export const customQueryKeys = {
  mapboxId: "mapboxId",
  timezoneLocation: "timezoneLocation",
  organization_id: "organization_id",
  permissions: "permissions",
} as const

export const userGuideLink =
  "https://drive.google.com/file/d/1j8fsRbzO4W7jiIzkKmEp4pSD28esP-7U/view?usp=sharing"

/**
 * Set by data engineering
 * use instead of riskThresholds
 */
// Remove when event tool is removed
export const RiskNames = {
  low: "Low",
  medium: "Moderate",
  high: "High",
  severe: "Severe",
} as const

export enum Permission {
  READ_USER = "read:user",
  WRITE_USER = "write:user",
  READ_ORGANIZATION = "read:organization",
  WRITE_ORGANIZATION = "write:organization",
  READ_ORGANIZATION_CURRENT = "read:organization.current",
  WRITE_ORGANIZATION_CURRENT = "write:organization.current",
  READ_PREFERENCES_USER_CURRENT = "read:preferences.user.current",
  WRITE_PREFERENCES_USER_CURRENT = "write:preferences.user.current",
  READ_PREFERENCES_USER = "read:preferences.user",
  WRITE_PREFERENCES_USER = "write:preferences.user",
  READ_PREFERENCES_USER_ALL = "read:preferences.user.all",
  WRITE_PREFERENCES_USER_ALL = "write:preferences.user.all",
  READ_PREFERENCES_ORGANIZATION = "read:preferences.organization",
  WRITE_PREFERENCES_ORGANIZATION = "write:preferences.organization",
  READ_PREFERENCES_ORGANIZATION_ALL = "read:preferences.organization.all",
  WRITE_PREFERENCES_ORGANIZATION_ALL = "write:preferences.organization.all",
  READ_NOTIFICATIONS = "read:notifications",
  WRITE_NOTIFICATIONS = "write:notifications",
}

export const userPermissions = [
  Permission.READ_PREFERENCES_USER_CURRENT,
  Permission.WRITE_PREFERENCES_USER_CURRENT,
  Permission.READ_NOTIFICATIONS,
  Permission.WRITE_NOTIFICATIONS,
]

export const adminPermissions = [
  Permission.READ_USER,
  Permission.WRITE_USER,
  Permission.READ_PREFERENCES_USER_CURRENT,
  Permission.WRITE_PREFERENCES_USER_CURRENT,
  Permission.READ_PREFERENCES_USER,
  Permission.READ_PREFERENCES_ORGANIZATION,
  Permission.WRITE_PREFERENCES_ORGANIZATION,
  Permission.READ_ORGANIZATION_CURRENT,
  Permission.READ_NOTIFICATIONS,
]

export const phcUserPermissions = [
  Permission.READ_USER,
  Permission.READ_ORGANIZATION,
  Permission.READ_PREFERENCES_USER_CURRENT,
  Permission.WRITE_PREFERENCES_USER_CURRENT,
  Permission.READ_PREFERENCES_ORGANIZATION,
  Permission.READ_NOTIFICATIONS,
  Permission.WRITE_NOTIFICATIONS,
]

export const phcAdminPermissions = [
  Permission.READ_USER,
  Permission.WRITE_USER,
  Permission.READ_ORGANIZATION,
  Permission.WRITE_ORGANIZATION,
  Permission.READ_PREFERENCES_USER_CURRENT,
  Permission.WRITE_PREFERENCES_USER_CURRENT,
  Permission.READ_PREFERENCES_USER,
  Permission.WRITE_PREFERENCES_USER,
  Permission.READ_PREFERENCES_USER_ALL,
  Permission.READ_PREFERENCES_ORGANIZATION,
  Permission.WRITE_PREFERENCES_ORGANIZATION,
  Permission.READ_PREFERENCES_ORGANIZATION_ALL,
  Permission.WRITE_PREFERENCES_ORGANIZATION_ALL,
  Permission.READ_NOTIFICATIONS,
  Permission.WRITE_NOTIFICATIONS,
]

export const TABLE_ROWS = 15

export enum AdminContentTags {
  SOURCES = "sources",
}

export enum SeverityLevel {
  Fatal = "fatal",
  Error = "error",
  Warning = "warning",
  Log = "log",
  Info = "info",
  Debug = "debug",
}

export const LEGAL_STATEMENT =
  "PHC Global provides this material for informational purposes only. It does not constitute, and is not a substitute for, medical services or advice and should not be construed as such."

export const LOCATION_IDS_WITH_STALE_DATA = [
  "586",
  "528",
  "704",
  "233",
  "496",
  "320",
  "372",
  "826",
  "332",
  "404",
  "800",
  "724",
]

export const TRUE = "true"
export const FALSE = "false"

export const STATIC_ASSET_URL =
  "https://assets.thepublichealthco.com/78d9d6c/sha-57f06f6/logos/"
